import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Enhances a slideshow that contains video slides:
 *   - Plays only the active slide's video, pauses the rest
 *   - Provides a sound toggle that controls every video at once
 *   - Pauses slide auto-rotation while sound is on
 *   - Optionally advances to the next slide when the active video ends
 *   - Supports uploaded videos plus YouTube and Vimeo iframes via postMessage
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [slideshow]
 * @property {HTMLButtonElement} [soundButton]
 *
 * @extends {Component<Refs>}
 */
class VideoSlideshow extends Component {
  #abortController = new AbortController();
  #activeIndex = 0;
  #muted = true;
  #desktopMQ = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;

    if (prefersReducedMotion()) {
      this.refs.slideshow?.removeAttribute('autoplay');
    }

    const slideshow = this.refs.slideshow;
    if (slideshow) {
      slideshow.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideSelect, { signal });
    }

    window.addEventListener('message', this.#handleVideoMessage, { signal });

    if (this.hasAttribute('advance-on-end')) {
      this.#wireUploadedVideoEndListeners(signal);
      this.#wirePlatformEndListeners(signal);
    }

    this.#observeVisibility(signal);

    // Listen for viewport changes (e.g. device rotation) to swap active video
    this.#desktopMQ.addEventListener('change', this.#handleViewportChange, { signal });

    queueMicrotask(() => {
      this.#initializeMedia();
      if (this.dataset.startUnmuted === 'true') {
        this.#setMuted(false, { fromUserGesture: false });
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /* -------------------------------------------------------------------------------------------- */
  /*  Public methods (declarative on:click handlers)                                              */
  /* -------------------------------------------------------------------------------------------- */

  toggleSound = () => {
    this.#setMuted(!this.#muted, { fromUserGesture: true });
  };

  /* -------------------------------------------------------------------------------------------- */
  /*  Initialization                                                                              */
  /* -------------------------------------------------------------------------------------------- */

  #slides() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('slideshow-slide')));
  }

  /**
   * Returns the media elements for the currently visible container in a slide.
   * When desktop/mobile containers exist, selects the one matching the viewport.
   */
  #mediaForSlide(slide) {
    if (!slide) return null;
    const containers = /** @type {NodeListOf<HTMLElement>} */ (slide.querySelectorAll('[data-video-slide]'));

    let container = null;
    if (containers.length > 1) {
      // Multiple containers: pick the one that is currently visible
      for (const c of containers) {
        if (getComputedStyle(c).display !== 'none') {
          container = c;
          break;
        }
      }
    }
    // Fallback to single container or first available
    if (!container) container = containers[0] ?? null;

    const video = /** @type {HTMLVideoElement | null} */ (container?.querySelector('video.video-slide__video') ?? null);
    const iframe = /** @type {HTMLIFrameElement | null} */ (container?.querySelector('iframe[data-video-type]') ?? null);
    return { video, iframe, container };
  }

  /**
   * Returns ALL media containers for a slide (both desktop and mobile if present).
   */
  #allMediaForSlide(slide) {
    if (!slide) return [];
    const containers = /** @type {NodeListOf<HTMLElement>} */ (slide.querySelectorAll('[data-video-slide]'));
    return Array.from(containers).map((container) => {
      const video = /** @type {HTMLVideoElement | null} */ (container.querySelector('video.video-slide__video'));
      const iframe = /** @type {HTMLIFrameElement | null} */ (container.querySelector('iframe[data-video-type]'));
      return { video, iframe, container };
    });
  }

  #initializeMedia() {
    const slides = this.#slides();
    slides.forEach((slide, index) => {
      // Initialize ALL containers (desktop + mobile) so both are ready to play
      const allMedia = this.#allMediaForSlide(slide);
      for (const { video, iframe, container } of allMedia) {
        if (video) {
          video.muted = true;
          video.playsInline = true;
          video.setAttribute('playsinline', '');
          video.preload = 'metadata';
          video.addEventListener(
            'loadeddata',
            () => container?.setAttribute('data-video-ready', 'true'),
            { signal: this.#abortController.signal, once: true }
          );
          video.addEventListener(
            'playing',
            () => container?.setAttribute('data-video-ready', 'true'),
            { signal: this.#abortController.signal, once: true }
          );
        }

        if (iframe && iframe.dataset.videoType === 'vimeo') {
          iframe.addEventListener(
            'load',
            () => this.#postToIframe(iframe, 'addEventListener', 'play'),
            { signal: this.#abortController.signal, once: true }
          );
        }
      }

      if (index === 0) {
        this.#playSlide(slide);
      } else {
        this.#pauseSlide(slide);
      }
    });
  }

  /* -------------------------------------------------------------------------------------------- */
  /*  Playback                                                                                    */
  /* -------------------------------------------------------------------------------------------- */

  #playSlide(slide) {
    const media = this.#mediaForSlide(slide);
    if (!media) return;
    const { video, iframe, container } = media;
    if (video) {
      try {
        video.currentTime = 0;
      } catch {}
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          // Some browsers reject when sound is on without a user gesture — fall back to muted.
          if (!video.muted) {
            video.muted = true;
            video.play().catch(() => {});
          }
        });
      }
      // Reveal the video over the poster as soon as a frame is ready.
      if (video.readyState >= 2) {
        container?.setAttribute('data-video-ready', 'true');
      }
    }
    if (iframe) {
      this.#postPlay(iframe);
    }
  }

  #pauseSlide(slide) {
    // Pause ALL containers in the slide (both desktop and mobile)
    const allMedia = this.#allMediaForSlide(slide);
    for (const { video, iframe } of allMedia) {
      if (video) {
        video.pause();
      }
      if (iframe) {
        this.#postPause(iframe);
      }
    }
  }

  #handleSlideSelect = (event) => {
    const detail = /** @type {{ index: number, previousIndex: number }} */ (event.detail);
    if (!detail) return;
    const { index } = detail;
    if (typeof index !== 'number') return;
    if (index === this.#activeIndex) return;

    const slides = this.#slides();
    const previousSlide = slides[this.#activeIndex];
    const nextSlide = slides[index];

    if (previousSlide) this.#pauseSlide(previousSlide);
    if (nextSlide) this.#playSlide(nextSlide);
    this.#activeIndex = index;
  };

  /**
   * When the viewport changes (e.g. device rotation), pause the outgoing
   * container's video and play the incoming container's video for the active slide.
   */
  #handleViewportChange = () => {
    const slides = this.#slides();
    const activeSlide = slides[this.#activeIndex];
    if (!activeSlide) return;

    // Pause all containers first
    const allMedia = this.#allMediaForSlide(activeSlide);
    for (const { video, iframe } of allMedia) {
      if (video) video.pause();
      if (iframe) this.#postPause(iframe);
    }

    // Play only the now-visible container
    this.#playSlide(activeSlide);

    // Re-apply mute state to the now-visible video
    if (!this.#muted) {
      const visibleMedia = this.#mediaForSlide(activeSlide);
      if (visibleMedia?.video) {
        visibleMedia.video.muted = false;
      }
      if (visibleMedia?.iframe) {
        this.#postMute(visibleMedia.iframe, false);
      }
    }
  };

  /* -------------------------------------------------------------------------------------------- */
  /*  Sound                                                                                       */
  /* -------------------------------------------------------------------------------------------- */

  #setMuted(muted, { fromUserGesture }) {
    this.#muted = muted;
    this.toggleAttribute('data-unmuted', !muted);

    const button = this.refs.soundButton;
    if (button) {
      button.setAttribute('aria-pressed', muted ? 'false' : 'true');
      // The aria-label always describes the action the button will perform next.
      const nextActionLabel = muted ? button.dataset.labelUnmute : button.dataset.labelMute;
      if (nextActionLabel) button.setAttribute('aria-label', nextActionLabel);
    }

    // Apply mute state only to the visible media in each slide
    const slides = this.#slides();
    for (const slide of slides) {
      const media = this.#mediaForSlide(slide);
      if (media?.video) {
        media.video.muted = muted;
        if (!muted && fromUserGesture) {
          media.video.play().catch(() => {});
        }
      }
      if (media?.iframe) {
        this.#postMute(media.iframe, muted);
      }
    }

    // When sound is on, pause auto-rotation so visitors can hear the active slide finish.
    const slideshow = this.refs.slideshow;
    if (slideshow && slideshow.hasAttribute('autoplay') && this.dataset.pauseRotationWhenUnmuted === 'true') {
      if (muted) {
        slideshow.removeAttribute?.('paused');
        // @ts-ignore — slideshow-component implements resume()
        slideshow.resume?.();
      } else {
        // @ts-ignore — slideshow-component implements pause()
        slideshow.pause?.();
      }
    }
  }

  /* -------------------------------------------------------------------------------------------- */
  /*  Advance on video end                                                                        */
  /* -------------------------------------------------------------------------------------------- */

  #wireUploadedVideoEndListeners(signal) {
    // Wire end listeners on ALL video elements (both desktop and mobile containers)
    // because only the visible one will actually play and fire 'ended'.
    this.querySelectorAll('video.video-slide__video').forEach((video) => {
      const el = /** @type {HTMLVideoElement} */ (video);
      el.loop = false;
      el.removeAttribute('loop');
      el.addEventListener(
        'ended',
        () => {
          // @ts-ignore — slideshow-component implements next()
          this.refs.slideshow?.next?.();
        },
        { signal }
      );
    });
  }

  #wirePlatformEndListeners(signal) {
    // YouTube: needs the iframe to have enablejsapi=1 (already set by the block).
    // We also need to register listening for state changes.
    this.querySelectorAll('iframe[data-video-type]').forEach((iframe) => {
      const el = /** @type {HTMLIFrameElement} */ (iframe);
      el.addEventListener(
        'load',
        () => {
          if (el.dataset.videoType === 'youtube') {
            el.contentWindow?.postMessage(
              JSON.stringify({ event: 'listening', id: 0, channel: 'widget' }),
              '*'
            );
          } else if (el.dataset.videoType === 'vimeo') {
            // Subscribe to Vimeo's `ended` event explicitly.
            this.#postToIframe(el, 'addEventListener', 'ended');
          }
        },
        { signal, once: true }
      );
    });
  }

  /* -------------------------------------------------------------------------------------------- */
  /*  Visibility — pause when scrolled off-screen                                                 */
  /* -------------------------------------------------------------------------------------------- */

  #observeVisibility(signal) {
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const slides = this.#slides();
          const activeSlide = slides[this.#activeIndex];
          if (!activeSlide) continue;
          if (entry.isIntersecting) {
            this.#playSlide(activeSlide);
          } else {
            this.#pauseSlide(activeSlide);
          }
        }
      },
      { threshold: 0.15 }
    );

    observer.observe(this);
    signal.addEventListener('abort', () => observer.disconnect());
  }

  /* -------------------------------------------------------------------------------------------- */
  /*  postMessage helpers (YouTube + Vimeo)                                                       */
  /* -------------------------------------------------------------------------------------------- */

  #postToIframe(iframe, methodOrFunc, value) {
    if (!iframe?.contentWindow) return;
    const type = iframe.dataset.videoType;
    /** @type {string} */
    let payload;
    if (type === 'youtube') {
      payload = JSON.stringify({
        event: 'command',
        func: methodOrFunc,
        args: value === undefined ? [] : [value],
      });
    } else {
      payload =
        value === undefined
          ? JSON.stringify({ method: methodOrFunc })
          : JSON.stringify({ method: methodOrFunc, value });
    }
    iframe.contentWindow.postMessage(payload, '*');
  }

  #postPlay(iframe) {
    if (!iframe) return;
    if (iframe.dataset.videoType === 'youtube') {
      this.#postToIframe(iframe, 'playVideo');
    } else {
      this.#postToIframe(iframe, 'play');
    }
  }

  #postPause(iframe) {
    if (!iframe) return;
    if (iframe.dataset.videoType === 'youtube') {
      this.#postToIframe(iframe, 'pauseVideo');
    } else {
      this.#postToIframe(iframe, 'pause');
    }
  }

  #postMute(iframe, muted) {
    if (!iframe) return;
    if (iframe.dataset.videoType === 'youtube') {
      this.#postToIframe(iframe, muted ? 'mute' : 'unMute');
    } else {
      this.#postToIframe(iframe, 'setVolume', muted ? 0 : 1);
    }
  }

  #handleVideoMessage = (event) => {
    if (typeof event.data !== 'string') return;
    if (!event.data.startsWith('{')) return;

    let data;
    try {
      data = JSON.parse(event.data);
    } catch {
      return;
    }

    // Identify the source iframe so we only react to messages from our own embeds.
    const iframes = this.querySelectorAll('iframe[data-video-type]');
    let sourceIframe = null;
    for (const candidate of iframes) {
      if (/** @type {HTMLIFrameElement} */ (candidate).contentWindow === event.source) {
        sourceIframe = /** @type {HTMLIFrameElement} */ (candidate);
        break;
      }
    }
    if (!sourceIframe) return;

    if (sourceIframe.dataset.videoType === 'youtube') {
      // YouTube state 0 == ended
      if (data.event === 'infoDelivery' && data.info?.playerState === 0) {
        if (this.hasAttribute('advance-on-end')) {
          // @ts-ignore — slideshow-component implements next()
          this.refs.slideshow?.next?.();
        }
      }
    } else if (sourceIframe.dataset.videoType === 'vimeo') {
      if (data.event === 'ended' && this.hasAttribute('advance-on-end')) {
        // @ts-ignore — slideshow-component implements next()
        this.refs.slideshow?.next?.();
      }
    }
  };
}

if (!customElements.get('video-slideshow')) {
  customElements.define('video-slideshow', VideoSlideshow);
}
